import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  FolderKanban,
  Users,
  FileText,
  ChevronRight,
  ChevronDown,
  BarChart3,
  Paperclip,
  Trash2,
  Edit,
  Calendar,
  Hash,
  Globe,
  Lock,
  CheckCircle,
  Activity,
  Layers,
  FolderOpen,
  ShoppingCart,
  FileSignature,
  Package,
  Settings,
  AlertTriangle,
  ArrowRight,
  StickyNote,
  ListChecks,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { t } from '@/i18n/ro';
import { useAuth } from '@/context/AuthContext';
import { getProject, getProjectMembers, getProjectAttachments, deleteProjectMember, type ProjectAttachment } from '@/services/projects.service';
import { getProjectWorkPackages, getStatusIdFromLink } from '@/services/workpackages.service';
import { useMetadata } from '@/context/MetadataContext';
import {
  Button,
  Card,
  Tabs,
  TabPanel,
  LoadingState,
  EmptyState,
  Avatar,
  Badge,
  Modal,
} from '@/components/common';
import { WorkPackageCard } from '@/components/dashboard/WorkPackageCard';
import { GanttChart } from './GanttChart';
import { ProjectActionsBar } from './ProjectActionsBar';
import { ProjectNotesPanel } from './ProjectNotesPanel';
import { AddMemberModal } from './AddMemberModal';
import { EditMemberRolesModal } from './EditMemberRolesModal';
import { ProjectFilesTable } from './ProjectFilesTable';
import DossierTab from '../dossier/DossierTab';
import TodoSection from '../dossier/TodoSection';
import { StatusMover, type StatusValue } from '@/components/common/StatusMover';
import type { Project, WorkPackage, Member, DossierOverview, ActivitiesSummary } from '@/types';
import { getDossierOverview, getProcurements, getContracts, getDeliverables, getEvents, getActivitiesSummary, setDossierStatus } from '@/services/dossier.service';
import type { DossierGanttItem } from './GanttChart';

// Tab-uri valide
const VALID_TABS = ['details', 'achizitii', 'contracte', 'livrabile', 'implementare', 'activitati', 'gantt', 'files', 'members'];

// Culoare de status pentru un modul de dosar (aceeași logică ca DossierTab)
type DossierTabColor = 'green' | 'yellow' | 'gray' | 'red';
function getDossierTabColor(overview: DossierOverview | null, module: string): DossierTabColor {
  if (!overview || !overview.exists) return 'gray';
  if (module === 'achizitii') {
    if (overview.achizitii.total === 0) return 'gray';
    if (overview.achizitii.completed === overview.achizitii.total) return 'green';
    if (overview.achizitii.inProgress > 0) return 'yellow';
    return 'gray';
  }
  if (module === 'contracte') {
    if (overview.contracte.total === 0) return 'gray';
    if (overview.contracte.remainingValue < 0) return 'red';
    return 'green';
  }
  if (module === 'livrabile') {
    if (overview.livrabile.total === 0) return 'gray';
    if (overview.livrabile.approved === overview.livrabile.total) return 'green';
    if (overview.livrabile.approved > 0 || overview.livrabile.completed > 0 || overview.livrabile.inProgress > 0) return 'yellow';
    return 'gray';
  }
  if (module === 'implementare') {
    if (overview.implementare.totalEvents === 0) return 'gray';
    return 'yellow';
  }
  return 'gray';
}

// Indicator numerotat (1-4) colorat după status, folosit ca „icon" pe taburile de dosar.
// Comunică vizual secvența lanțului și unde a rămas utilizatorul.
function TabStepIcon({ num, color }: { num: number; color: DossierTabColor }) {
  const colorClasses: Record<DossierTabColor, string> = {
    green: 'bg-green-100 text-green-700 border-green-400',
    yellow: 'bg-amber-100 text-amber-700 border-amber-400',
    red: 'bg-red-100 text-red-700 border-red-400',
    gray: 'bg-gray-100 text-gray-500 border-gray-300',
  };
  return (
    <span
      className={`inline-flex items-center justify-center w-5 h-5 rounded-full border text-[11px] font-bold ${colorClasses[color]}`}
      aria-hidden="true"
    >
      {num}
    </span>
  );
}

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { statusGroups } = useMetadata();

  const [project, setProject] = useState<Project | null>(null);
  const [workPackages, setWorkPackages] = useState<WorkPackage[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [projectFiles, setProjectFiles] = useState<ProjectAttachment[]>([]);
  const [dossierOverview, setDossierOverview] = useState<DossierOverview | null>(null);
  // Reîncarcă sumarul dosarului (badge-uri taburi, „De făcut", overview) după orice
  // adăugare/ștergere într-un modul, ca cifrele să nu rămână învechite.
  const reloadDossierOverview = () => {
    getDossierOverview(Number(projectId)).then(setDossierOverview).catch(() => {});
  };
  // Schimbă statusul dosarului (dossier_instances.status) și reflectează imediat:
  // aplică pe backend, actualizează badge-ul și reîncarcă overview + progresul din activități.
  const handleDossierStatusChange = async (next: StatusValue) => {
    if (!dossierId) return;
    const applied = await setDossierStatus(dossierId, next);
    setDossierStatusState(normalizeStatus(applied));
    reloadDossierOverview();
    getActivitiesSummary(dossierId).then(setActivitiesSummary).catch(() => {});
  };
  const [dossierGanttItems, setDossierGanttItems] = useState<DossierGanttItem[]>([]);
  // Progresul dosarului se calculează acum din dossier.activities (nu din work packages OpenProject,
  // acum goale). Rezumat: total / completed / inProgress / todo + procent.
  const [activitiesSummary, setActivitiesSummary] = useState<ActivitiesSummary | null>(null);
  // Statusul mutabil la nivel de dosar (dossier_instances.status), default 'in_progress'.
  const [dossierStatus, setDossierStatusState] = useState<StatusValue>('in_progress');
  // id-ul dossier_instance, luat din overview-ul deja încărcat; necesar pentru StatusMover și progres.
  const dossierId = dossierOverview?.exists ? dossierOverview.dossier?.id : undefined;
  // Normalizează un status arbitrar la una din valorile cunoscute (fallback 'in_progress').
  const normalizeStatus = (s?: string | null): StatusValue =>
    s === 'pending' || s === 'in_progress' || s === 'completed' ? s : 'in_progress';
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [memberToEditRoles, setMemberToEditRoles] = useState<Member | null>(null);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);
  const [memberDeleteReason, setMemberDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [notesRefresh, setNotesRefresh] = useState(0);
  // Numărul de note, raportat de ProjectNotesPanel. Cât timp e null (se încarcă)
  // sau 0, ascundem complet secțiunea „Note proiect" — panoul rămâne montat ca
  // să încarce și să raporteze, dar cardul apare doar când există note.
  const [notesCount, setNotesCount] = useState<number | null>(null);

  // Funcție pentru reîncărcarea membrilor
  const refreshMembers = async () => {
    if (!projectId) return;
    try {
      const membersData = await getProjectMembers(Number(projectId));
      setMembers(membersData);
    } catch (err) {
      console.error('Failed to refresh members:', err);
    }
  };

  // Funcție pentru reîncărcarea activităților
  const refreshWorkPackages = async () => {
    if (!projectId) return;
    try {
      const wpData = await getProjectWorkPackages(Number(projectId));
      setWorkPackages(wpData);
    } catch (err) {
      console.error('Failed to refresh work packages:', err);
    }
  };

  // Funcție pentru ștergerea unui membru
  const handleDeleteMember = async () => {
    if (!memberToDelete || !projectId) return;

    setIsDeleting(true);
    try {
      await deleteProjectMember(Number(projectId), memberToDelete.id, memberDeleteReason || undefined);
      await refreshMembers();
      setMemberToDelete(null);
      setMemberDeleteReason('');
      toast.success('Membrul a fost eliminat din proiect.');
    } catch (err) {
      console.error('Failed to delete member:', err);
      toast.error('Nu s-a putut elimina membrul. Încearcă din nou.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Verifică dacă utilizatorul curent poate gestiona membrii (Admin proiect SAU admin global)
  const isProjectAdmin = useMemo(() => {
    if (!user) return false;

    // Administratorii globali pot gestiona membrii oricărui proiect
    if (user.admin) return true;

    if (!members.length) return false;

    // Găsim membership-ul utilizatorului curent
    const currentUserMembership = members.find(m => {
      const principal = m._embedded?.principal;
      return principal?.id === user.id;
    });

    if (!currentUserMembership) return false;

    // Verificăm dacă are rol de "Admin proiect" sau "Project admin"
    const roles = currentUserMembership._embedded?.roles || [];
    return roles.some(role =>
      role.name.toLowerCase().includes('admin') ||
      role.name.toLowerCase().includes('manager')
    );
  }, [user, members]);

  // Membru al proiectului (orice rol) — necesar pentru a putea marca „Urgent"
  const isProjectMember = useMemo(() => {
    if (!user || !members.length) return false;
    return members.some(m => m._embedded?.principal?.id === user.id);
  }, [user, members]);

  // Calcul progres dosar: (activități finalizate) / (total activități native).
  // Sursa e dossier.activities (via getActivitiesSummary), NU work packages OpenProject
  // (acum goale). Nu există categorie „respinse" la activitățile native.
  const projectProgress = useMemo(() => {
    const s = activitiesSummary;
    if (!s || s.total === 0) {
      return { total: 0, completed: 0, inProgress: 0, todo: 0, percent: 0 };
    }
    return {
      total: s.total,
      completed: s.completed,
      inProgress: s.inProgress,
      todo: s.todo,
      percent: s.percent,
    };
  }, [activitiesSummary]);

  // Responsabil proiect: primul membru cu rol de admin/manager/responsabil
  const projectResponsible = useMemo(() => {
    const m = members.find((mm) =>
      (mm._embedded?.roles || []).some((r) => {
        const n = r.name.toLowerCase();
        return n.includes('admin') || n.includes('manager') || n.includes('responsabil');
      })
    );
    if (!m) return null;
    const p = m._embedded?.principal;
    if (p) {
      return `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.login;
    }
    return m._links.principal?.title || null;
  }, [members]);

  // Funcție pentru reîncărcarea fișierelor
  const refreshFiles = async () => {
    if (!projectId) return;
    try {
      const filesData = await getProjectAttachments(Number(projectId));
      setProjectFiles(filesData);
    } catch (err) {
      console.error('Failed to refresh files:', err);
    }
  };

  // Citește tab-ul din hash sau folosește default
  const getTabFromHash = () => {
    const hash = location.hash.replace('#', '');
    return VALID_TABS.includes(hash) ? hash : 'details';
  };

  const [activeTab, setActiveTab] = useState(getTabFromHash);

  // Sincronizează tab-ul cu hash-ul din URL
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (VALID_TABS.includes(hash) && hash !== activeTab) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Handler pentru schimbarea tab-ului
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    // Actualizează URL-ul cu hash-ul noului tab
    navigate(`/projects/${projectId}#${tabId}`, { replace: true });
  };

  // Încărcare date
  useEffect(() => {
    const loadData = async () => {
      if (!projectId) return;

      setIsLoading(true);
      setError(null);

      try {
        const [projectData, wpData, membersData, filesData] = await Promise.all([
          getProject(Number(projectId)),
          getProjectWorkPackages(Number(projectId)),
          getProjectMembers(Number(projectId)),
          getProjectAttachments(Number(projectId)),
        ]);

        setProject(projectData);
        setWorkPackages(wpData);
        setMembers(membersData);
        setProjectFiles(filesData);

        // Load dossier data for Gantt and Details
        try {
          const dOverview = await getDossierOverview(Number(projectId));
          setDossierOverview(dOverview);
          if (dOverview.exists && dOverview.dossier) {
            const did = dOverview.dossier.id;
            // Statusul dosarului: preferă valoarea din overview/dossier; fallback 'in_progress'.
            setDossierStatusState(normalizeStatus(dOverview.dossier.status ?? dOverview.status));
            // Progresul dosarului din activități native: folosește sumarul din overview dacă
            // backend-ul îl trimite, altfel îl calculează încărcând activitățile.
            if (dOverview.activitiesSummary) {
              setActivitiesSummary(dOverview.activitiesSummary);
            } else {
              getActivitiesSummary(did).then(setActivitiesSummary).catch(() => {});
            }
            const [procs, contracts, deliverables, events] = await Promise.all([
              getProcurements(did),
              getContracts(did),
              getDeliverables(did),
              getEvents(did),
            ]);
            const items: DossierGanttItem[] = [];
            procs.forEach(p => {
              if (p.start_date || p.end_date) {
                items.push({ id: `proc-${p.id}`, label: p.name, category: 'achizitii', startDate: p.start_date || null, endDate: p.end_date || null, status: p.status });
              }
            });
            contracts.forEach(c => {
              if (c.start_date || c.end_date) {
                items.push({ id: `contr-${c.id}`, label: c.contract_name, category: 'contracte', startDate: c.start_date || null, endDate: c.end_date || null, status: c.status });
              }
            });
            deliverables.forEach(d => {
              if (d.due_date) {
                items.push({ id: `deliv-${d.id}`, label: d.name, category: 'livrabile', startDate: d.due_date, endDate: d.due_date, status: d.status });
              }
            });
            events.forEach(e => {
              if (e.event_date || e.expiry_date) {
                items.push({ id: `event-${e.id}`, label: e.description || `Eveniment #${e.id}`, category: 'implementare', startDate: e.event_date || null, endDate: e.expiry_date || e.event_date || null, status: e.status });
              }
            });
            setDossierGanttItems(items);
          }
        } catch (err) {
          console.error('Failed to load dossier data:', err);
        }
      } catch (err) {
        console.error('Failed to load project data:', err);
        setError(t('errors.loadFailed'));
        toast.error('Nu s-au putut încărca datele proiectului.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [projectId]);

  // Taburile de dosar sunt numerotate 1-4 (secvența lanțului) și primesc un
  // indicator de status colorat + badge cu numărul de elemente din dossierOverview.
  const tabs = [
    {
      id: 'details',
      label: t('projects.dossierTabs.details'),
      icon: <FileText className="w-4 h-4" />,
    },
    {
      id: 'achizitii',
      label: t('projects.dossierTabs.achizitii'),
      icon: <TabStepIcon num={1} color={getDossierTabColor(dossierOverview, 'achizitii')} />,
      badge: dossierOverview?.achizitii?.total ?? 0,
    },
    {
      id: 'contracte',
      label: t('projects.dossierTabs.contracte'),
      icon: <TabStepIcon num={2} color={getDossierTabColor(dossierOverview, 'contracte')} />,
      badge: dossierOverview?.contracte?.total ?? 0,
    },
    {
      id: 'livrabile',
      label: t('projects.dossierTabs.livrabile'),
      icon: <TabStepIcon num={3} color={getDossierTabColor(dossierOverview, 'livrabile')} />,
      badge: dossierOverview?.livrabile?.total ?? 0,
    },
    {
      id: 'implementare',
      label: t('projects.dossierTabs.implementare'),
      icon: <TabStepIcon num={4} color={getDossierTabColor(dossierOverview, 'implementare')} />,
      badge: dossierOverview?.implementare?.totalEvents ?? 0,
    },
    {
      id: 'activitati',
      label: 'Activități',
      icon: <ListChecks className="w-4 h-4" />,
    },
    {
      id: 'gantt',
      label: t('projects.dossierTabs.gantt'),
      icon: <BarChart3 className="w-4 h-4" />,
    },
    {
      id: 'files',
      label: t('projects.dossierTabs.files'),
      icon: <Paperclip className="w-4 h-4" />,
      badge: projectFiles.length,
    },
    {
      id: 'members',
      label: t('projects.dossierTabs.members'),
      icon: <Users className="w-4 h-4" />,
      badge: members.length,
    },
  ];

  if (isLoading) {
    return <LoadingState message={t('common.loading')} />;
  }

  if (error || !project) {
    return (
      <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
        {error || t('errors.notFound')}
      </div>
    );
  }

  // Formatare dată pentru header în format DD/MM/YYYY
  const formatHeaderDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          {t('common.back')}
        </Button>
      </div>

      {/* Header Section - stil similar cu WorkPackageDetailPage */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium border bg-primary-100 text-primary-700 border-primary-200">
                <FolderKanban className="w-4 h-4" />
                <span>Proiect</span>
              </div>
              {project.active !== false ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm font-medium bg-green-100 text-green-700 border border-green-200">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Activ
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-600 border border-gray-200">
                  Inactiv
                </span>
              )}
              {project.public ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-700 border border-blue-200">
                  <Globe className="w-3.5 h-3.5" />
                  Public
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-600 border border-gray-200">
                  <Lock className="w-3.5 h-3.5" />
                  Privat
                </span>
              )}
              {project._links.status?.title && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                  <Activity className="w-3.5 h-3.5" />
                  {project._links.status.title}
                </span>
              )}
              {/* Status dosar — mutabil cu ◀ ▶ (pending → in_progress → completed) */}
              {dossierOverview?.exists && dossierId && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-sm text-gray-500">Status dosar:</span>
                  <StatusMover
                    value={dossierStatus}
                    onChange={handleDossierStatusChange}
                    showLabel
                    size="sm"
                  />
                </span>
              )}
            </div>

            {/* Titlu */}
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
              {project.name}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 text-gray-500 text-sm">
              <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-mono text-xs">
                {project.identifier}
              </span>
              {project.createdAt && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Creat: {formatHeaderDate(project.createdAt)}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {members.length} membri
              </span>
              {projectProgress.total > 0 && (
                <span className="flex items-center gap-1">
                  <Layers className="w-4 h-4" />
                  {projectProgress.total} activități
                </span>
              )}
            </div>

            {/* Rând informativ: responsabil, valoare contract, termen-limită */}
            {(() => {
              const cf = ((project as any).customFields || {}) as Record<string, any>;
              const contractValue = cf.pretContract
                ? (() => {
                    const num = parseFloat(cf.pretContract);
                    return isNaN(num)
                      ? String(cf.pretContract)
                      : new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(num);
                  })()
                : null;
              const deadline = formatHeaderDate(cf.dueDate);
              return (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-600 mt-2">
                  <span className="flex items-center gap-1.5">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">{t('projects.header.responsible')}:</span>
                    <span className="font-medium text-gray-800">
                      {projectResponsible || t('projects.header.noResponsible')}
                    </span>
                  </span>
                  {contractValue && (
                    <span className="flex items-center gap-1.5">
                      <FileSignature className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500">{t('projects.header.contractValue')}:</span>
                      <span className="font-semibold text-primary-700">{contractValue}</span>
                    </span>
                  )}
                  {deadline && (
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500">{t('projects.header.deadline')}:</span>
                      <span className="font-medium text-gray-800">{deadline}</span>
                    </span>
                  )}
                </div>
              );
            })()}

            {/* Bară progres dosar — calculată din statusul activităților native (dossier.activities) */}
            {projectProgress.total > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-gray-700">Progres dosar</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {projectProgress.completed}/{projectProgress.total} finalizate · {projectProgress.percent}%
                  </span>
                </div>
                <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      projectProgress.percent >= 100 ? 'bg-green-500' :
                      projectProgress.percent >= 50 ? 'bg-primary-500' :
                      projectProgress.percent > 0 ? 'bg-orange-500' : 'bg-gray-300'
                    }`}
                    style={{ width: `${projectProgress.percent}%` }}
                  />
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-400" />
                    {projectProgress.todo} de făcut
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-blue-400" />
                    {projectProgress.inProgress} în lucru
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    {projectProgress.completed} finalizate
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Acțiuni */}
          <div className="flex flex-col items-stretch lg:items-end gap-3">
            {/* Butoane FAZA 2: Urgent / Urmărește / Note — vizibile tuturor cu acces */}
            <ProjectActionsBar
              projectId={Number(projectId)}
              currentUserId={user?.id}
              canSetUrgent={isProjectMember || isProjectAdmin}
              onNotesChanged={() => setNotesRefresh(n => n + 1)}
            />
            {isProjectAdmin && (
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Edit className="w-4 h-4" />}
                  onClick={() => navigate(`/projects/${projectId}/edit`)}
                >
                  Editează proiect
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Achiziții */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-100">
              <ShoppingCart className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Achiziții</p>
              <p className="font-semibold text-gray-900">{dossierOverview?.achizitii?.total ?? 0}</p>
            </div>
          </div>
        </div>

        {/* Contracte */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-100">
              <FileSignature className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Contracte</p>
              <p className="font-semibold text-gray-900">{dossierOverview?.contracte?.total ?? 0}</p>
            </div>
          </div>
        </div>

        {/* Membri */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Membri</p>
              <p className="font-semibold text-gray-900">{members.length}</p>
            </div>
          </div>
        </div>

        {/* Fișiere */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <Paperclip className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Fișiere</p>
              <p className="font-semibold text-gray-900">{projectFiles.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Rezumat „De făcut" — activează codul mort: ghidează utilizatorul spre pașii rămași */}
      {/* „De făcut" apare pe taburile de dosar; pe tabul Detalii e acoperit de cardul „Următorul pas", ca să nu dublăm ghidarea. */}
      {dossierOverview?.exists && activeTab !== 'details' && <TodoSection overview={dossierOverview} />}

      {/* Note proiect — inline, sus în pagină (nu în modal); ștergibile de autor.
          Cardul e afișat doar când există note; panoul rămâne mereu montat ca să
          încarce și să raporteze numărul (altfel n-ar afla niciodată câte sunt). */}
      <div className={notesCount && notesCount > 0 ? "bg-white rounded-xl p-5 shadow-sm border border-gray-100" : "hidden"}>
        <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <StickyNote className="w-4 h-4 text-amber-600" /> Note proiect
        </h2>
        <ProjectNotesPanel projectId={Number(projectId)} currentUserId={user?.id} mode="list" refreshSignal={notesRefresh} onCountChange={setNotesCount} />
      </div>

      {/* Tabs */}
      <Card padding="none">
        <div className="px-4 pt-4">
          <Tabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
        </div>

        <div className="p-4">
          {/* Tab: Gantt */}
          <TabPanel tabId="gantt" activeTab={activeTab}>
            <GanttChart workPackages={workPackages} dossierItems={dossierGanttItems} />
          </TabPanel>

          {/* Tab: Fișiere Proiect */}
          <TabPanel tabId="files" activeTab={activeTab}>
            <ProjectFilesTable
              projectId={Number(projectId)}
              attachments={projectFiles}
              currentUserId={user?.id}
              onFileDeleted={refreshFiles}
            />
          </TabPanel>

          {/* Tab: Membri */}
          <TabPanel tabId="members" activeTab={activeTab}>
            <div className="space-y-4">
              {isProjectAdmin && (
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => setShowAddMemberModal(true)}
                >
                  {t('projects.actions.addMember')}
                </Button>
              )}

              {members.length === 0 ? (
                <EmptyState
                  icon="users"
                  title="Nu există membri"
                  description="Acest proiect nu are membri definiți."
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Nume</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Rol în proiect</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 w-32">Tip membru</th>
                        {isProjectAdmin && (
                          <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 w-24">Acțiuni</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {members.map((member) => (
                        <MemberRow
                          key={member.id}
                          member={member}
                          canDelete={isProjectAdmin}
                          canEditRoles={isProjectAdmin}
                          currentUserId={user?.id}
                          onDelete={() => setMemberToDelete(member)}
                          onEditRoles={() => setMemberToEditRoles(member)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabPanel>

          {/* Tab: Achiziții */}
          <TabPanel tabId="achizitii" activeTab={activeTab}>
            <DossierTab projectId={Number(projectId)} section="achizitii" onOverviewChange={reloadDossierOverview} />
          </TabPanel>

          {/* Tab: Contracte */}
          <TabPanel tabId="contracte" activeTab={activeTab}>
            <DossierTab projectId={Number(projectId)} section="contracte" onOverviewChange={reloadDossierOverview} />
          </TabPanel>

          {/* Tab: Livrabile */}
          <TabPanel tabId="livrabile" activeTab={activeTab}>
            <DossierTab projectId={Number(projectId)} section="livrabile" onOverviewChange={reloadDossierOverview} />
          </TabPanel>

          {/* Tab: Implementare */}
          <TabPanel tabId="implementare" activeTab={activeTab}>
            <DossierTab projectId={Number(projectId)} section="implementare" onOverviewChange={reloadDossierOverview} />
          </TabPanel>

          {/* Tab: Activități */}
          <TabPanel tabId="activitati" activeTab={activeTab}>
            <DossierTab projectId={Number(projectId)} section="activitati" onOverviewChange={reloadDossierOverview} />
          </TabPanel>

          {/* Tab: Detalii */}
          <TabPanel tabId="details" activeTab={activeTab}>
            <ProjectDetails
              project={project}
              workPackages={workPackages}
              projectFiles={projectFiles}
              members={members}
              dossierOverview={dossierOverview}
              onNavigateToTab={handleTabChange}
            />
          </TabPanel>
        </div>
      </Card>

      {/* Modal adăugare membru */}
      <AddMemberModal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        projectId={Number(projectId)}
        onMemberAdded={refreshMembers}
      />

      <EditMemberRolesModal
        isOpen={!!memberToEditRoles}
        onClose={() => setMemberToEditRoles(null)}
        projectId={Number(projectId)}
        member={memberToEditRoles}
        onRolesUpdated={refreshMembers}
      />

      {/* Modal confirmare ștergere membru */}
      <Modal
        isOpen={!!memberToDelete}
        onClose={() => {
          setMemberToDelete(null);
          setMemberDeleteReason('');
        }}
        title="Confirmare ștergere"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => {
              setMemberToDelete(null);
              setMemberDeleteReason('');
            }}>
              Anulează
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteMember}
              isLoading={isDeleting}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              Șterge
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Sunteți sigur că doriți să eliminați membrul{' '}
            <strong>
              {memberToDelete?._embedded?.principal
                ? `${memberToDelete._embedded.principal.firstName || ''} ${memberToDelete._embedded.principal.lastName || ''}`.trim() || memberToDelete._embedded.principal.login
                : memberToDelete?._links.principal?.title || 'selectat'}
            </strong>{' '}
            din acest proiect?
          </p>
          <p className="text-sm text-gray-500">
            Această acțiune nu poate fi anulată.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motiv ștergere (opțional)
            </label>
            <textarea
              value={memberDeleteReason}
              onChange={(e) => setMemberDeleteReason(e.target.value)}
              placeholder="Introduceți motivul ștergerii..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              rows={3}
            />
          </div>
        </div>
      </Modal>

    </div>
  );
}

// Componenta pentru fază cu copii
interface PhaseItemProps {
  phase: WorkPackage;
  children: WorkPackage[];
  onPhaseClick: () => void;
  onChildClick: (wp: WorkPackage) => void;
  onAddTask: () => void;
}

function PhaseItem({
  phase,
  children,
  onPhaseClick,
  onChildClick,
  onAddTask,
}: PhaseItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div
        className="flex items-center gap-3 p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <button className="p-0.5">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </button>
        <div className="flex-1" onClick={(e) => { e.stopPropagation(); onPhaseClick(); }}>
          <span className="font-medium text-gray-900 hover:text-primary-700 cursor-pointer">
            {phase.subject}
          </span>
        </div>
        <Badge variant="default" size="sm">
          {children.length} activități
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onAddTask();
          }}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {isExpanded && children.length > 0 && (
        <div className="p-3 space-y-2 bg-white">
          {children.map((child) => (
            <WorkPackageCard
              key={child.id}
              workPackage={child}
              onClick={() => onChildClick(child)}
              showProject={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Componenta pentru rând membru
interface MemberRowProps {
  member: Member;
  canDelete?: boolean;
  canEditRoles?: boolean;
  currentUserId?: number;
  onDelete?: () => void;
  onEditRoles?: () => void;
}

function MemberRow({ member, canDelete, canEditRoles, currentUserId, onDelete, onEditRoles }: MemberRowProps) {
  const principal = member._embedded?.principal;
  const roles = member._embedded?.roles || [];

  const name = principal
    ? `${principal.firstName || ''} ${principal.lastName || ''}`.trim() || principal.login
    : member._links.principal?.title || 'Unknown';

  // Determinăm tipul de membru din _type sau din link-ul principal
  const principalType = (principal as any)?._type || '';
  const memberType = principalType === 'Group' ? 'Grup' :
                     principalType === 'User' ? 'Utilizator' :
                     principalType || 'Utilizator';

  // Nu permitem ștergerea propriei persoane
  const isSelf = principal?.id === currentUserId;

  return (
    <tr className="hover:bg-gray-50">
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <Avatar name={name} size="sm" />
          <div>
            <p className="font-medium text-gray-900">{name}</p>
            {principal?.email && (
              <p className="text-xs text-gray-500">{principal.email}</p>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        <div className="flex flex-wrap gap-1.5">
          {roles.map((role) => (
            <Badge key={role.id} variant="primary" size="sm">
              {role.name}
            </Badge>
          ))}
        </div>
      </td>
      <td className="py-3 px-4">
        <span className="text-sm text-gray-600">{memberType}</span>
      </td>
      {(canDelete || canEditRoles) && (
        <td className="py-3 px-4 text-center">
          <div className="flex items-center justify-center gap-1">
            {canEditRoles && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onEditRoles}
                title="Modifică rolurile"
              >
                <Edit className="w-4 h-4 text-primary-600" />
              </Button>
            )}
            {canDelete && !isSelf && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                title="Șterge membru"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

// Funcție helper pentru a parsa Markdown simplu în HTML
function parseMarkdown(text: string): string {
  if (!text) return '';

  let html = text
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Headers
    .replace(/^### (.+)$/gm, '<h4 class="text-base font-bold text-gray-800 mt-4 mb-2">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 class="text-lg font-bold text-gray-900 mt-4 mb-2">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 class="text-xl font-bold text-gray-900 mt-4 mb-3">$1</h2>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Lists - procesare pe linii
    .split('\n')
    .map(line => {
      // Liste cu buline
      if (line.match(/^- (.+)$/)) {
        return line.replace(/^- (.+)$/, '<li class="ml-4 mb-1 flex items-start"><span class="text-primary-600 mr-2">•</span><span>$1</span></li>');
      }
      // Liste numerotate
      if (line.match(/^\d+\. (.+)$/)) {
        return line.replace(/^(\d+)\. (.+)$/, '<li class="ml-4 mb-1"><span class="font-medium text-gray-700 mr-2">$1.</span>$2</li>');
      }
      // Linii goale -> paragraf nou
      if (line.trim() === '') {
        return '<div class="h-2"></div>';
      }
      return line;
    })
    .join('\n')
    // Wrap consecutive li items in ul
    .replace(/(<li[^>]*>.*?<\/li>\n?)+/g, (match) => {
      return `<ul class="space-y-1 my-2">${match}</ul>`;
    })
    // Line breaks pentru text normal (nu headers sau liste)
    .replace(/([^>\n])\n([^<\n])/g, '$1<br/>$2');

  return html;
}

// Componenta pentru afișare Markdown formatat
function MarkdownContent({ content }: { content: string }) {
  const html = parseMarkdown(content);

  return (
    <div
      className="text-gray-700 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// Componenta pentru un rând de informație
interface InfoRowProps {
  label: string;
  value: React.ReactNode;
  isHtml?: boolean;
}

function InfoRow({ label, value, isHtml }: InfoRowProps) {
  if (!value || value === '-') return null;

  return (
    <div className="py-3 border-b border-gray-100 last:border-b-0">
      <dt className="text-sm font-bold text-gray-700 mb-1">
        {label}
      </dt>
      {isHtml ? (
        <dd
          className="text-gray-900 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: value as string }}
        />
      ) : (
        <dd className="text-gray-900">{value}</dd>
      )}
    </div>
  );
}

// Componenta pentru detalii proiect
interface ProjectDetailsProps {
  project: Project;
  workPackages: WorkPackage[];
  projectFiles: ProjectAttachment[];
  members: Member[];
  dossierOverview: DossierOverview | null;
  onNavigateToTab: (tabId: string) => void;
}

function ProjectDetails({ project, workPackages, projectFiles, members, dossierOverview, onNavigateToTab }: ProjectDetailsProps) {
  const navigate = useNavigate();

  // Formatare dată în format DD/MM/YYYY
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };

  // Formatare preț
  const formatPrice = (price?: string) => {
    if (!price) return null;
    const num = parseFloat(price);
    if (isNaN(num)) return price;
    return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(num);
  };

  // Custom fields
  const customFields = (project as any).customFields || {};

  // Ultimele 5 fișiere (sortate după dată de creare)
  const recentFiles = useMemo(() => {
    return [...projectFiles]
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      })
      .slice(0, 5);
  }, [projectFiles]);

  // „Următorul pas": semnalează dezechilibrele lanțului Achiziție → Contract → Livrabile → Implementare
  const nextSteps = useMemo(() => {
    if (!dossierOverview?.exists) return [];
    const steps: { message: string; tab: string }[] = [];
    const a = dossierOverview.achizitii;
    const c = dossierOverview.contracte;
    const l = dossierOverview.livrabile;

    if (a.total === 0) {
      steps.push({ message: t('projects.nextStep.startProcurement'), tab: 'achizitii' });
    } else if (c.total === 0) {
      steps.push({
        message: `Ai ${a.total} ${a.total === 1 ? 'achiziție' : 'achiziții'} dar niciun contract. Atribuie un contract.`,
        tab: 'contracte',
      });
    }
    if (c.total > 0 && l.total === 0) {
      steps.push({
        message: `Ai ${c.total} ${c.total === 1 ? 'contract' : 'contracte'} dar niciun livrabil. Adaugă un livrabil.`,
        tab: 'livrabile',
      });
    }
    const activeAlerts = dossierOverview.alerts.critical + dossierOverview.alerts.warning;
    if (activeAlerts > 0) {
      steps.push({
        message: `${activeAlerts} ${activeAlerts === 1 ? 'termen necesită atenție' : 'termene necesită atenție'} în implementare.`,
        tab: 'implementare',
      });
    }
    return steps;
  }, [dossierOverview]);

  return (
    <div className="space-y-6">
      {/* Privire de ansamblu activă: card „Următorul pas" citind dossierOverview */}
      {dossierOverview?.exists && (
        <div className={`rounded-lg border p-4 ${nextSteps.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
          <h3 className={`text-sm font-semibold flex items-center gap-2 mb-2 ${nextSteps.length > 0 ? 'text-amber-800' : 'text-green-800'}`}>
            {nextSteps.length > 0 ? (
              <AlertTriangle className="w-4 h-4" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            {t('projects.nextStep.title')}
          </h3>
          {nextSteps.length > 0 ? (
            <ul className="space-y-2">
              {nextSteps.map((step, i) => (
                <li key={i} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-700">{step.message}</span>
                  <button
                    onClick={() => onNavigateToTab(step.tab)}
                    className="flex-shrink-0 inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 font-medium"
                  >
                    {t('projects.nextStep.goToTab')}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-green-700">{t('projects.nextStep.allGood')}</p>
          )}
        </div>
      )}
      {/* Descriere proiect - dacă există */}
      {(project.description?.html || project.description?.raw) && (
        <div className="bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 p-5">
          <div className="prose prose-sm max-w-none">
            <MarkdownContent content={project.description?.raw || ''} />
          </div>
        </div>
      )}

      {/* Grid 2 coloane: Date Contract + Informații Proiect */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Coloana stânga: Date Contract */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-primary-50 px-4 py-3 border-b border-primary-100">
            <h3 className="text-base font-semibold text-primary-800 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Date Contract
            </h3>
          </div>
          <div className="p-4 space-y-3">
            {customFields.nrInregistrare && (
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-500">Nr. înregistrare</span>
                <span className="text-sm font-medium text-gray-900">{customFields.nrInregistrare}</span>
              </div>
            )}
            {customFields.dataSemnarii && (
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-500">Data semnării</span>
                <span className="text-sm font-medium text-gray-900">{formatDate(customFields.dataSemnarii)}</span>
              </div>
            )}
            {customFields.durataContract && (
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-500">Durata contract</span>
                <span className="text-sm font-medium text-gray-900">{customFields.durataContract}</span>
              </div>
            )}
            {customFields.pretContract && (
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-500">Preț contract</span>
                <span className="text-sm font-semibold text-primary-700">{formatPrice(customFields.pretContract)}</span>
              </div>
            )}
            {customFields.partileContractante && (
              <div className="pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-500 block mb-1">Părțile contractante</span>
                <p className="text-sm text-gray-900 whitespace-pre-line">{customFields.partileContractante}</p>
              </div>
            )}
            {!customFields.nrInregistrare && !customFields.dataSemnarii && !customFields.durataContract && !customFields.pretContract && !customFields.partileContractante && (
              <p className="text-sm text-gray-500 italic">Nu sunt completate date de contract.</p>
            )}
          </div>
        </div>

        {/* Coloana dreapta: Informații Proiect */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Informații Proiect
            </h3>
          </div>
          <div className="p-4 space-y-3">
            {customFields.startDate && (
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-500">Data de început</span>
                <span className="text-sm font-medium text-gray-900">{formatDate(customFields.startDate)}</span>
              </div>
            )}
            {customFields.dueDate && (
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-500">Data limită</span>
                <span className="text-sm font-medium text-gray-900">{formatDate(customFields.dueDate)}</span>
              </div>
            )}
            <div className="flex justify-between items-start">
              <span className="text-sm text-gray-500">Data creării</span>
              <span className="text-sm font-medium text-gray-900">{formatDate(project.createdAt)}</span>
            </div>
            {project.updatedAt && project.updatedAt !== project.createdAt && (
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-500">Ultima actualizare</span>
                <span className="text-sm font-medium text-gray-900">{formatDate(project.updatedAt)}</span>
              </div>
            )}
            {project._links.parent?.title && (
              <div className="pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-500 block mb-1">Proiect părinte</span>
                <span className="text-sm font-medium text-primary-700">{project._links.parent.title}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dossier Summary */}
      {dossierOverview?.exists && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Achiziții */}
          <button
            onClick={() => onNavigateToTab('achizitii')}
            className="bg-white rounded-lg border border-gray-200 p-4 text-left hover:border-orange-300 hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded bg-orange-100">
                <ShoppingCart className="w-4 h-4 text-orange-600" />
              </div>
              <h4 className="text-sm font-semibold text-gray-800">Achiziții</h4>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total</span>
                <span className="font-medium">{dossierOverview.achizitii.total}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Finalizate</span>
                <span className="font-medium text-green-600">{dossierOverview.achizitii.completed}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">În progres</span>
                <span className="font-medium text-yellow-600">{dossierOverview.achizitii.inProgress}</span>
              </div>
            </div>
          </button>

          {/* Contracte */}
          <button
            onClick={() => onNavigateToTab('contracte')}
            className="bg-white rounded-lg border border-gray-200 p-4 text-left hover:border-indigo-300 hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded bg-indigo-100">
                <FileSignature className="w-4 h-4 text-indigo-600" />
              </div>
              <h4 className="text-sm font-semibold text-gray-800">Contracte</h4>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total</span>
                <span className="font-medium">{dossierOverview.contracte.total}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Valoare</span>
                <span className="font-medium">{new Intl.NumberFormat('ro-RO').format(dossierOverview.contracte.totalValue)} RON</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Facturat</span>
                <span className="font-medium text-blue-600">{new Intl.NumberFormat('ro-RO').format(dossierOverview.contracte.invoicedTotal)} RON</span>
              </div>
            </div>
          </button>

          {/* Livrabile */}
          <button
            onClick={() => onNavigateToTab('livrabile')}
            className="bg-white rounded-lg border border-gray-200 p-4 text-left hover:border-teal-300 hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded bg-teal-100">
                <Package className="w-4 h-4 text-teal-600" />
              </div>
              <h4 className="text-sm font-semibold text-gray-800">Livrabile</h4>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total</span>
                <span className="font-medium">{dossierOverview.livrabile.total}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Aprobate</span>
                <span className="font-medium text-green-600">{dossierOverview.livrabile.approved}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">În așteptare</span>
                <span className="font-medium text-yellow-600">{dossierOverview.livrabile.pending}</span>
              </div>
            </div>
          </button>

          {/* Implementare */}
          <button
            onClick={() => onNavigateToTab('implementare')}
            className="bg-white rounded-lg border border-gray-200 p-4 text-left hover:border-rose-300 hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded bg-rose-100">
                <Settings className="w-4 h-4 text-rose-600" />
              </div>
              <h4 className="text-sm font-semibold text-gray-800">Implementare</h4>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Evenimente</span>
                <span className="font-medium">{dossierOverview.implementare.totalEvents}</span>
              </div>
              {(dossierOverview.alerts.critical > 0 || dossierOverview.alerts.warning > 0) && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Alerte</span>
                  <span className="font-medium text-red-600">
                    {dossierOverview.alerts.critical + dossierOverview.alerts.warning}
                  </span>
                </div>
              )}
            </div>
          </button>
        </div>
      )}

      {/* Preview: Ultimele fișiere */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-orange-50 px-4 py-3 border-b border-orange-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-orange-800 flex items-center gap-2">
            <Paperclip className="w-4 h-4" />
            Fișiere recente
            <span className="text-xs font-normal text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
              {projectFiles.length} total
            </span>
          </h3>
          <button
            onClick={() => onNavigateToTab('files')}
            className="text-sm text-orange-700 hover:text-orange-800 font-medium flex items-center gap-1"
          >
            Vezi toate
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="divide-y divide-gray-100">
          {recentFiles.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              Nu există fișiere în acest proiect.
            </div>
          ) : (
            recentFiles.map((file) => (
              <div
                key={file.id}
                className="px-4 py-3 hover:bg-gray-50 flex items-center justify-between"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-8 h-8 rounded bg-gray-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.fileName}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(file.createdAt)} • {(file.fileSize / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <a
                  href={file._links.downloadLocation?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  Descarcă
                </a>
              </div>
            ))
          )}
        </div>
        {projectFiles.length > 5 && (
          <div className="bg-gray-50 px-4 py-2 text-center border-t border-gray-100">
            <button
              onClick={() => onNavigateToTab('files')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              + {projectFiles.length - 5} alte fișiere
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
